import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import { socketService } from "../../../services/socket";
import { useAuth } from "../../auth/contexts/AuthContext";
import { useProjects } from "../../common/contexts/ProjectContext";
import { Button } from "@/components/ui/button";

interface BugReportButtonProps {
  module: string;
  accentColor?: string;
}

export const BugReportButton: React.FC<BugReportButtonProps> = ({
  module,
  accentColor: _accentColor = "#F72D60",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<"critical" | "medium" | "minor">("medium");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [, setCapturing] = useState(false);
  const { user } = useAuth();
  const { activeProject } = useProjects();

  const captureScreenshot = async () => {
    try {
      setCapturing(true);
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#0a0a1a",
      });
      const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
      setScreenshot(dataUrl);
    } catch (e) {
      console.warn("Screenshot capture failed:", e);
    } finally {
      setCapturing(false);
    }
  };

  const openModal = async () => {
    await captureScreenshot();
    setIsOpen(true);
  };

  const handleSubmit = () => {
    if (!description.trim()) return;
    setSending(true);

    const metadata = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
      projectName: activeProject?.name || null,
    };

    socketService.emit("submit-bug-report", {
      projectId: activeProject?.id || null,
      userId: user?.id || null,
      userEmail: (user as any)?.email || null,
      userName: user?.name || null,
      module,
      severity,
      description: description.trim(),
      screenshot: screenshot || null,
      metadata,
    });

    setTimeout(() => {
      setSending(false);
      setSent(true);
      setTimeout(() => {
        setIsOpen(false);
        setSent(false);
        setDescription("");
        setSeverity("medium");
        setScreenshot(null);
      }, 1500);
    }, 500);
  };

  const severities = [
    {
      id: "critical" as const,
      label: t("common.bugReport.critical"),
      iconClass: "bx bxs-error-circle",
      color: "#EF4444",
      desc: t("common.bugReport.criticalDesc"),
    },
    {
      id: "medium" as const,
      label: t("common.bugReport.annoying"),
      iconClass: "bx bxs-error",
      color: "#F59E0B",
      desc: t("common.bugReport.annoyingDesc"),
    },
    {
      id: "minor" as const,
      label: t("common.bugReport.minor"),
      iconClass: "bx bxs-info-circle",
      color: "#10B981",
      desc: t("common.bugReport.minorDesc"),
    },
  ];

  return (
    <>
      {/* Bug Button */}
      <Button
        variant="outline"
        size="icon"
        onClick={openModal}
        title={t("common.bugReport.reportTitle")}
        className="text-muted-foreground hover:text-[#F72D60] hover:border-[#F72D60]/30 hover:bg-[#F72D60]/10"
      >
        <i className="bx bx-bug" style={{ fontSize: "1.1rem" }}></i>
      </Button>

      {/* Modal — rendered via portal to escape overflow:hidden containers */}
      {isOpen &&
        createPortal(
          <div
            onClick={() => !sending && setIsOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.75)",
              backdropFilter: "blur(12px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 99999,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "linear-gradient(145deg, #1a1a2e, #161625)",
                border: "1px solid rgba(247, 45, 96, 0.4)",
                borderRadius: "16px",
                padding: "30px",
                width: "440px",
                maxWidth: "90vw",
                maxHeight: "85vh",
                overflowY: "auto",
                boxShadow:
                  "0 25px 80px rgba(0,0,0,0.6), 0 0 60px rgba(247, 45, 96, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)",
              }}
            >
              {sent ? (
                /* Success State */
                <div style={{ textAlign: "center", padding: "30px 0" }}>
                  <div style={{ fontSize: "3rem", marginBottom: "15px", color: "#10B981" }}>
                    <i className="bx bx-check-circle"></i>
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: 900,
                      color: "#10B981",
                      letterSpacing: "1px",
                    }}
                  >
                    {t("common.bugReport.bugReported")}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--color-text-muted, #888)",
                      marginTop: "8px",
                    }}
                  >
                    {t("common.bugReport.thankYou")}
                  </div>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <i className="bx bx-bug" style={{ fontSize: "1.5rem", color: "#F72D60" }}></i>
                    <div>
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: 900,
                          letterSpacing: "1.5px",
                          color: "#F72D60",
                        }}
                      >
                        {t("common.bugReport.reportBug")}
                      </div>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          color: "var(--color-text-muted, #888)",
                          marginTop: "2px",
                        }}
                      >
                        Module: {module.toUpperCase()} • {user?.name || "User"}
                      </div>
                    </div>
                    <button
                      onClick={() => setIsOpen(false)}
                      style={{
                        marginLeft: "auto",
                        background: "none",
                        border: "none",
                        color: "#888",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        padding: "4px",
                      }}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  </div>

                  {/* Severity */}
                  <div style={{ marginBottom: "20px" }}>
                    <div
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        letterSpacing: "0.5px",
                        color: "var(--color-text-muted, #888)",
                        marginBottom: "8px",
                      }}
                    >
                      {t("common.bugReport.severity")}
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {severities.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setSeverity(s.id)}
                          style={{
                            flex: 1,
                            padding: "10px 8px",
                            background:
                              severity === s.id
                                ? s.id === "critical"
                                  ? "rgba(239, 68, 68, 0.15)"
                                  : s.id === "medium"
                                    ? "rgba(245, 158, 11, 0.15)"
                                    : "rgba(16, 185, 129, 0.15)"
                                : "rgba(255,255,255,0.03)",
                            border:
                              severity === s.id
                                ? `1px solid ${s.id === "critical" ? "rgba(239, 68, 68, 0.5)" : s.id === "medium" ? "rgba(245, 158, 11, 0.5)" : "rgba(16, 185, 129, 0.5)"}`
                                : "1px solid rgba(255,255,255,0.08)",
                            borderRadius: "8px",
                            cursor: "pointer",
                            textAlign: "center",
                            transition: "all 0.2s",
                          }}
                        >
                          <div style={{ fontSize: "1.3rem", color: s.color }}>
                            <i className={s.iconClass}></i>
                          </div>
                          <div
                            style={{
                              fontSize: "0.6rem",
                              fontWeight: 800,
                              letterSpacing: "0.5px",
                              marginTop: "4px",
                              color: severity === s.id ? "#fff" : "#888",
                            }}
                          >
                            {s.label}
                          </div>
                          <div style={{ fontSize: "0.55rem", color: "#666", marginTop: "2px" }}>
                            {s.desc}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: "20px" }}>
                    <div
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        letterSpacing: "0.5px",
                        color: "var(--color-text-muted, #888)",
                        marginBottom: "8px",
                      }}
                    >
                      {t("common.bugReport.whatHappened")}
                    </div>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("common.bugReport.describePlaceholder")}
                      autoFocus
                      style={{
                        width: "100%",
                        minHeight: "100px",
                        padding: "12px",
                        background: "rgba(0,0,0,0.3)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "10px",
                        color: "#fff",
                        fontSize: "0.85rem",
                        fontFamily: "inherit",
                        resize: "vertical",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = "rgba(247, 45, 96, 0.4)")
                      }
                      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
                    />
                  </div>

                  {/* Auto-attached info */}
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.05)",
                      marginBottom: "20px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.6rem",
                        color: "#666",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="bx bx-devices" style={{ fontSize: "0.75rem" }}></i>{" "}
                        {module.toUpperCase()}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="bx bx-user" style={{ fontSize: "0.75rem" }}></i>{" "}
                        {(user as any)?.email || user?.name || "—"}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="bx bx-folder" style={{ fontSize: "0.75rem" }}></i>{" "}
                        {activeProject?.name || "—"}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <i className="bx bx-time-five" style={{ fontSize: "0.75rem" }}></i>{" "}
                        {new Date().toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Screenshot Preview */}
                  {screenshot && (
                    <div style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: 800,
                          letterSpacing: "0.5px",
                          color: "var(--color-text-muted, #888)",
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <i className="bx bx-camera" style={{ fontSize: "0.8rem" }}></i>{" "}
                        {t("common.bugReport.screenshot")}
                      </div>
                      <div
                        style={{
                          position: "relative",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "1px solid rgba(255,255,255,0.1)",
                        }}
                      >
                        <img
                          src={screenshot}
                          alt="Screenshot"
                          style={{
                            width: "100%",
                            height: "120px",
                            objectFit: "cover",
                            display: "block",
                            filter: "brightness(0.8)",
                          }}
                        />
                        <button
                          onClick={() => setScreenshot(null)}
                          style={{
                            position: "absolute",
                            top: "6px",
                            right: "6px",
                            background: "rgba(0,0,0,0.7)",
                            border: "none",
                            color: "#fff",
                            width: "22px",
                            height: "22px",
                            borderRadius: "50%",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                          }}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    onClick={handleSubmit}
                    disabled={!description.trim() || sending}
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: description.trim()
                        ? "linear-gradient(135deg, #F72D60, #D9265A)"
                        : "rgba(255,255,255,0.05)",
                      border: "none",
                      borderRadius: "10px",
                      color: description.trim() ? "#fff" : "#666",
                      fontSize: "0.8rem",
                      fontWeight: 900,
                      letterSpacing: "1px",
                      cursor: description.trim() ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      transition: "all 0.2s",
                      opacity: sending ? 0.7 : 1,
                    }}
                  >
                    {sending ? (
                      <>
                        <i className="bx bx-loader-alt bx-spin"></i> {t("common.bugReport.sending")}
                      </>
                    ) : (
                      <>
                        <i className="bx bx-send"></i> {t("common.bugReport.submitReport")}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
